/**
 * Platform auth API — the mechanism ported from grant_management.
 *
 * `getBranding` is the platform-vs-tenant signal: the server returns a `name`
 * only when the request carried a tenant (a subdomain). The frontend never
 * inspects `window.location.host`; it branches on this response.
 */
import apiClient from './client';

export interface TenantBranding {
  name: string;
  subdomain: string;
  description: string;
  address: string;
  phone: string;
  tax_id: string;
  logo: string | null;
  logo_alt: string;
  logo_size: number;
  header_logo_height: number | null;
  footer_logo: string | null;
  footer_logo_alt: string;
  footer_logo_height: number | null;
  title_font_size: number | null;
  title_text_transform: '' | 'uppercase' | 'small-caps';
  title_bold: boolean;
  title_italic: boolean;
  title_max_width: number | null;
  footer_heading: string;
  footer_tagline: string;
  footer_doxology: string;
  primary_color: string;
  background_color: string;
  text_color: string;
  border_color: string;
  accent_color: string;
  font_pairing: string;
  // When false, the tenant subdomain root serves a minimal landing instead of
  // the full CMS site (server-driven; never host-parsed). Toggled in the admin
  // Site section.
  full_site_enabled: boolean;
}

export interface UserCapabilities {
  is_platform_admin: boolean;
  is_org_admin: boolean;
  is_org_user: boolean;
  /** A school's own login (a partner account) — routed to /partners/dashboard. */
  is_partner: boolean;
}

export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  role: string;
  role_display: string;
  organization: number | null;
  organization_name: string | null;
  capabilities: UserCapabilities;
}

export interface TenantContext {
  id: number;
  name: string;
  subdomain: string;
}

export interface SignupRequest {
  organization_name: string;
  email: string;
  first_name?: string;
  last_name?: string;
  subdomain?: string;
}

export interface SignupResponse {
  subdomain: string;
  login_url: string;
  message: string;
}

export const authApi = {
  getBranding: (): Promise<TenantBranding> =>
    apiClient.get('/accounts/branding/').then((r) => r.data),

  sendMagicLink: (email: string, nextUrl: string) =>
    apiClient
      .post('/accounts/auth/magic-link/send/', { email, next_url: nextUrl })
      .then((r) => r.data),

  verifyMagicLink: (token: string) =>
    apiClient
      .get(`/accounts/auth/magic-link/verify/?token=${encodeURIComponent(token)}`)
      .then((r) => r.data),

  currentUser: (): Promise<{
    user: AuthUser;
    profile: null;
    tenant_context: TenantContext | null;
  }> => apiClient.get('/accounts/auth/current_user/').then((r) => r.data),

  logout: () => apiClient.post('/accounts/auth/logout/').then((r) => r.data),

  signup: (data: SignupRequest): Promise<SignupResponse> =>
    apiClient
      .post('/accounts/organizations/signup/', data)
      .then((r) => r.data),
};
